/**
 * API Response Models for searchVisuals endpoint
 */

/**
 * Interface for alternative prompt in the response
 */
export interface AlternativePrompt {
  music: string;
  reasoning: string;
}

/**
 * Interface for alternative briefs section
 */
export interface AlternativeBriefs {
  content: string;
  prompts: AlternativePrompt[];
}

/**
 * Interface for criteria section (optional)
 */
export interface SearchCriteria {
  narrative?: string;
  tags?: string[];
}

/**
 * Interface for search result item
 */
export interface SearchResult {
  // Common properties across all results
  start_time: number;
  score: number;
  artist: string;
  id: string;
  title: string;
  url: string;

  // Optional properties that may be present
  spotify_id?: string;
  youtube_id?: string;
  end_time?: number;
  cover_url?: string;
  section_lyrics?: string;
  large_image_url?: string;
  webp_url?: string;
}

/**
 * Interface for the main data section of the response
 */
export interface SearchVisualsData {
  brief: string;
  alternative_briefs: AlternativeBriefs;
  criteria?: SearchCriteria;
  reasoning: string | null;
  results: SearchResult[];
}

/**
 * Interface for error information
 */
export interface ApiError {
  code: string;
  message: string;
}

/**
 * Main interface for searchVisuals API response
 */
export interface SearchVisualsResponse {
  data: SearchVisualsData;
  status: string;
  error?: ApiError;
}

/**
 * Type guard to check if response has results
 */
export function hasResults(response: SearchVisualsResponse): boolean {
  return response.data?.results?.length > 0;
}

/**
 * Type guard to check if response has error
 */
export function hasError(response: SearchVisualsResponse): boolean {
  return !!response.error;
}

/**
 * Type guard to check if it's a "no matches" error
 */
export function isNoMatchesError(response: SearchVisualsResponse): boolean {
  return hasError(response) &&
    response.error!.message.includes('We could not find good matches for your query');
}

// ============================================================================
// REFRESH SEARCH (TEXT SEARCH) INTERFACES
// ============================================================================

/**
 * Interface for refresh search result item (text-based search)
 */
export interface RefreshSearchResult {
  start_time: number;
  score: number;
  section_lyrics: string;
  artist: string;
  spotify_id: string;
  end_time: number;
  id: string;
  title: string;
  youtube_id: string;
  url: string;
}

/**
 * Interface for the main data section of the refresh search response
 */
export interface RefreshSearchData {
  results: RefreshSearchResult[];
}

/**
 * Main interface for refreshSearch API response
 */
export interface RefreshSearchResponse {
  data: RefreshSearchData;
  status: string;
  error?: ApiError;
}

/**
 * Type guard to check if refresh search response has results
 */
export function hasRefreshResults(response: RefreshSearchResponse): boolean {
  return response.data?.results?.length > 0;
}

/**
 * Type guard to check if refresh search response has error
 */
export function hasRefreshError(response: RefreshSearchResponse): boolean {
  return !!response.error;
}

/**
 * Type guard to check if it's a "no matches" error for refresh search
 */
export function isRefreshNoMatchesError(response: RefreshSearchResponse): boolean {
  return hasRefreshError(response) &&
    response.error!.message.includes('We could not find good matches for your query');
}

// ============================================================================
// GAMING MODE / CONTEXTUAL SEARCH INTERFACES
// ============================================================================

/**
 * Interface for song result from contextual search (gaming mode)
 */
export interface GamingSong {
  score: number;
  large_image_url: string;
  cover_url: string;
  artist: string;
  spotify_id: string;
  contact: string;
  isPopular: boolean;
  id: number;
  title: string;
  youtube_id: string;
  url: string;
  webp_url: string;
  lyrics?: string;
}

/**
 * Interface for contextual search data (gaming mode)
 */
export interface GamingResponseData {
  brief?: string;
  criteria?: any;
  results: GamingSong[];
}

/**
 * Interface for contextual search response (gaming mode)
 */
export interface GamingResponse {
  data: GamingResponseData;
  status: string;
}

/**
 * Type guard to check if response has gaming songs
 */
export function hasGamingSongs(response: GamingResponse): boolean {
  return response.data?.results?.length > 0;
}
